import {
  Stack,
  Title,
  Paper,
  Text,
  Table,
  Checkbox,
  Textarea,
  NumberInput,
  Group,
  Button,
  Badge,
  Divider,
  Loader,
  Center,
  Alert,
} from "@mantine/core";
import "@mantine/core/styles.css";
import { useEffect, useMemo, useState } from "react";
import { notifications } from "@mantine/notifications";
import { IconInfoCircle } from "@tabler/icons-react";
import { useTranslation } from "../../../hooks/useTranslation";
import { usePermissions } from "../../../hooks/usePermissions";
import useTransientStore from "../../../data/transcientStore";
import {
  getSelfDelivery,
  saveSelfDelivery,
  approveSelfDelivery,
  releaseSelfDelivery,
  addSelfDeliveryComment,
  SelfDelivery,
  SelfDeliveryCheckItem,
} from "../../../services/SelfDeliveryService";

export interface SelfDeliveryTabProps {
  form: any;
  addressSystemId?: number;
  userId?: number;
  canModify?: boolean;
}

// Local editable copy of a checklist row.
type CheckRow = SelfDeliveryCheckItem;

const formatDate = (value: string | null | undefined): string => {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString("fi-FI");
};

export default function SelfDeliveryTab({
  form,
  addressSystemId,
  userId,
  canModify = true,
}: Readonly<SelfDeliveryTabProps>) {
  const selectedAddressSystem = useTransientStore(
    (state) => state.selectedAddressSystem,
  );
  const addrIdFromStore = selectedAddressSystem?.id || 0;

  const { getTranslation } = useTranslation({
    pageId: 721,
    addressSystemId: addrIdFromStore || 0,
    autoLoad: true,
  });

  const { checkAccess } = usePermissions();
  // Editing is governed by the modal-wide object-modify permission (canModify prop,
  // from CITY_OBJECT_MODIFY). The two self-delivery-specific DB functions are
  // CITY_OBJECT_SELFDELIVERY_APPROVE and CITY_OBJECT_SELFDELIVERY_COMMENTS_SHOW.
  const canModifyRight = canModify;
  const canApprove = checkAccess("CITY_OBJECT_SELFDELIVERY_APPROVE");
  const canShowComments = checkAccess("CITY_OBJECT_SELFDELIVERY_COMMENTS_SHOW");

  // Derive object/contract ids from the shared form (mirrors DiaryTab).
  const addrId =
    addressSystemId !== undefined && addressSystemId > 0
      ? addressSystemId
      : addrIdFromStore;

  const contractId: number | undefined = useMemo(() => {
    const explicit = form?.values?.contractId;
    if (typeof explicit === "number" && explicit > 0) return explicit;
    const job = form?.values?.job;
    if (typeof job === "string" && job.length > 0) {
      const parsed = parseInt(job, 10);
      return Number.isNaN(parsed) ? undefined : parsed;
    }
    return undefined;
  }, [form?.values?.contractId, form?.values?.job]);

  const objectId: number | undefined = useMemo(() => {
    const explicit = form?.values?.objectId;
    if (typeof explicit === "number" && explicit > 0) return explicit;
    const objId = form?.values?.id;
    if (typeof objId === "number" && objId > 0) return objId;
    return undefined;
  }, [form?.values?.objectId, form?.values?.id]);

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [data, setData] = useState<SelfDelivery | null>(null);

  // Editable state
  const [reviewChecks, setReviewChecks] = useState<CheckRow[]>([]);
  const [pavementChecks, setPavementChecks] = useState<CheckRow[]>([]);
  const [subObjectIds, setSubObjectIds] = useState<number[]>([]);
  const [pavementInformation, setPavementInformation] = useState("");
  const [streetPermitCost, setStreetPermitCost] = useState<number | string>("");
  const [otherCost, setOtherCost] = useState<number | string>("");
  const [otherCostInformation, setOtherCostInformation] = useState("");
  const [roadMarkingInformation, setRoadMarkingInformation] = useState("");
  const [subObjectInformation, setSubObjectInformation] = useState("");
  const [areaChecked, setAreaChecked] = useState(false);
  const [scheduleChecked, setScheduleChecked] = useState(false);
  const [materialsChecked, setMaterialsChecked] = useState(false);
  const [checkInformation, setCheckInformation] = useState("");
  const [deviationInformation, setDeviationInformation] = useState("");
  const [newComment, setNewComment] = useState("");

  const hydrate = (d: SelfDelivery) => {
    setData(d);
    setReviewChecks(d.reviewChecks.map((r) => ({ ...r })));
    setPavementChecks(d.pavementChecks.map((r) => ({ ...r })));
    setSubObjectIds(d.subObjects.filter((s) => s.includeInSelfDelivery).map((s) => s.id));
    setPavementInformation(d.pavementInformation ?? "");
    setStreetPermitCost(d.streetPermitCost ?? "");
    setOtherCost(d.otherCost ?? "");
    setOtherCostInformation(d.otherCostInformation ?? "");
    setRoadMarkingInformation(d.roadMarkingInformation ?? "");
    setSubObjectInformation(d.subObjectInformation ?? "");
    setAreaChecked(d.areaChecked);
    setScheduleChecked(d.scheduleChecked);
    setMaterialsChecked(d.materialsChecked);
    setCheckInformation(d.checkInformation ?? "");
    setDeviationInformation(d.deviationInformation ?? "");
  };

  const loadData = () => {
    if (!contractId || !objectId || !addrId || !userId) return;
    setLoading(true);
    getSelfDelivery(objectId, contractId, userId, addrId)
      .then((d) => hydrate(d))
      .catch(() => {
        notifications.show({
          color: "red",
          message:
            getTranslation("MESSAGE_LOAD_FAILED") ||
            "Failed to load self delivery",
        });
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contractId, objectId, addrId, userId]);

  // Editable only while a draft (status 0) and the user has the modify right.
  const editable = !!data && data.canModify && canModifyRight;

  const updateCheckRow = (
    setter: React.Dispatch<React.SetStateAction<CheckRow[]>>,
    index: number,
    patch: Partial<CheckRow>,
  ) => {
    setter((rows) =>
      rows.map((r, i) => (i === index ? { ...r, ...patch } : r)),
    );
  };

  const buildSaveRequest = (saveFinal: boolean) => ({
    objectId: objectId!,
    contractId: contractId!,
    userId: userId!,
    addressSystemId: addrId!,
    saveFinal,
    pavementInformation: pavementInformation || null,
    streetPermitCost:
      streetPermitCost === "" ? null : Number(streetPermitCost),
    otherCost: otherCost === "" ? null : Number(otherCost),
    otherCostInformation: otherCostInformation || null,
    roadMarkingInformation: roadMarkingInformation || null,
    subObjectInformation: subObjectInformation || null,
    areaChecked,
    scheduleChecked,
    materialsChecked,
    checkInformation: checkInformation || null,
    deviationInformation: deviationInformation || null,
    subObjectIds,
    checkValues: [...reviewChecks, ...pavementChecks].map((r) => ({
      lookupDataId: r.lookupDataId,
      valueChecked: r.valueChecked,
      deviation: r.deviation || null,
      correctiveAction: r.correctiveAction || null,
      responsiblePerson: r.responsiblePerson || null,
      information: r.information || null,
    })),
  });

  const handleSave = (saveFinal: boolean) => {
    if (!objectId || !contractId || !addrId || !userId) return;
    setSaving(true);
    saveSelfDelivery(buildSaveRequest(saveFinal))
      .then(() => {
        notifications.show({
          color: "green",
          message: getTranslation("MESSAGE_SAVED") || "Self delivery saved",
        });
        loadData();
      })
      .catch(() => {
        notifications.show({
          color: "red",
          message:
            getTranslation("MESSAGE_SAVE_FAILED") ||
            "Failed to save self delivery",
        });
      })
      .finally(() => setSaving(false));
  };

  const handleWorkflow = (action: "approve" | "release") => {
    if (!data || !objectId || !contractId || !addrId || !userId) return;
    const confirmText =
      action === "approve"
        ? getTranslation("ALERT_APPROVE_SELF_DELIVERY") ||
          "Are you sure you want to approve this self delivery?"
        : getTranslation("ALERT_RELEASE_SELF_DELIVERY") ||
          "Are you sure you want to release this self delivery?";
    if (!window.confirm(confirmText)) return;

    const request = {
      id: data.id,
      objectId,
      contractId,
      userId,
      addressSystemId: addrId,
    };
    setSaving(true);
    const call =
      action === "approve"
        ? approveSelfDelivery(request)
        : releaseSelfDelivery(request);
    call
      .then(() => {
        notifications.show({
          color: "green",
          message:
            action === "approve"
              ? getTranslation("MESSAGE_APPROVED") || "Self delivery approved"
              : getTranslation("MESSAGE_RELEASED") || "Self delivery released",
        });
        loadData();
      })
      .catch(() => {
        notifications.show({
          color: "red",
          message:
            getTranslation("MESSAGE_ACTION_FAILED") || "Action failed",
        });
      })
      .finally(() => setSaving(false));
  };

  const handleAddComment = () => {
    if (!data || data.id <= 0 || !objectId || !contractId || !addrId || !userId)
      return;
    if (!newComment.trim()) return;
    setSaving(true);
    addSelfDeliveryComment({
      id: data.id,
      objectId,
      contractId,
      userId,
      addressSystemId: addrId,
      commentText: newComment.trim(),
    })
      .then((comment) => {
        setData((prev) =>
          prev ? { ...prev, comments: [...prev.comments, comment] } : prev,
        );
        setNewComment("");
      })
      .catch(() => {
        notifications.show({
          color: "red",
          message:
            getTranslation("MESSAGE_COMMENT_FAILED") ||
            "Failed to save comment",
        });
      })
      .finally(() => setSaving(false));
  };

  const statusLabel = (status: number): string => {
    const key = `LABEL_STATUS_${status}`;
    const fallback =
      status === 0 ? "Draft" : status === 1 ? "Submitted" : "Approved";
    return getTranslation(key) || fallback;
  };

  const statusColor = (status: number) =>
    status === 2 ? "green" : status === 1 ? "yellow" : "gray";

  if (!objectId || !contractId) {
    return (
      <Alert icon={<IconInfoCircle size={16} />} color="blue" variant="light">
        {getTranslation("MESSAGE_SELECT_CONTRACT_OBJECT") ||
          "Select a contract and object to view self delivery."}
      </Alert>
    );
  }

  if (loading) {
    return (
      <Center py="xl">
        <Loader />
      </Center>
    );
  }

  if (!data) {
    return (
      <Alert icon={<IconInfoCircle size={16} />} color="blue" variant="light">
        {getTranslation("MESSAGE_NO_RECORD") ||
          "No self delivery data available for this object."}
      </Alert>
    );
  }

  const renderChecklist = (
    title: string,
    rows: CheckRow[],
    setter: React.Dispatch<React.SetStateAction<CheckRow[]>>,
  ) => {
    if (rows.length === 0) return null;
    return (
      <Paper withBorder p="md" radius="md">
        <Title order={5} mb="sm">
          {title}
        </Title>
        <Table striped withTableBorder withColumnBorders>
          <Table.Thead>
            <Table.Tr>
              <Table.Th style={{ width: 260 }}>
                {getTranslation("LABEL_ITEMS_TO_VERIFY") || "Items to verify"}
              </Table.Th>
              <Table.Th style={{ width: 60 }}>
                {getTranslation("LABEL_OK") || "OK"}
              </Table.Th>
              <Table.Th>
                {getTranslation("LABEL_DEVIATION") || "Deviation found"}
              </Table.Th>
              <Table.Th>{getTranslation("LABEL_ACTION") || "Action"}</Table.Th>
              <Table.Th>
                {getTranslation("LABEL_RESPONSIBLE_PERSON") ||
                  "Responsible person"}
              </Table.Th>
              <Table.Th>
                {getTranslation("LABEL_ADDITIONAL_INFO") ||
                  "Additional information"}
              </Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {rows.map((row, index) => (
              <Table.Tr key={row.lookupDataId}>
                <Table.Td>
                  <Text fw={600} size="sm">
                    {row.description}
                  </Text>
                </Table.Td>
                <Table.Td>
                  <Checkbox
                    checked={row.valueChecked}
                    disabled={!editable}
                    onChange={(e) =>
                      updateCheckRow(setter, index, {
                        valueChecked: e.currentTarget.checked,
                      })
                    }
                  />
                </Table.Td>
                <Table.Td>
                  <Textarea
                    autosize
                    minRows={1}
                    value={row.deviation ?? ""}
                    disabled={!editable}
                    onChange={(e) =>
                      updateCheckRow(setter, index, {
                        deviation: e.currentTarget.value,
                      })
                    }
                  />
                </Table.Td>
                <Table.Td>
                  <Textarea
                    autosize
                    minRows={1}
                    value={row.correctiveAction ?? ""}
                    disabled={!editable}
                    onChange={(e) =>
                      updateCheckRow(setter, index, {
                        correctiveAction: e.currentTarget.value,
                      })
                    }
                  />
                </Table.Td>
                <Table.Td>
                  <Textarea
                    autosize
                    minRows={1}
                    value={row.responsiblePerson ?? ""}
                    disabled={!editable}
                    onChange={(e) =>
                      updateCheckRow(setter, index, {
                        responsiblePerson: e.currentTarget.value,
                      })
                    }
                  />
                </Table.Td>
                <Table.Td>
                  <Textarea
                    autosize
                    minRows={1}
                    value={row.information ?? ""}
                    disabled={!editable}
                    onChange={(e) =>
                      updateCheckRow(setter, index, {
                        information: e.currentTarget.value,
                      })
                    }
                  />
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </Paper>
    );
  };

  const showSaveButtons = editable && data.selfDeliveryStatus === 0;
  const showApproveButtons =
    canApprove &&
    !data.acceptedByName &&
    data.selfDeliveryStatus === 1;
  const showReleaseButton =
    canApprove && !!data.acceptedByName && data.selfDeliveryStatus === 2;

  return (
    <Stack gap="md">
      {/* Header / object information */}
      <Paper withBorder p="md" radius="md">
        <Group justify="space-between" mb="sm">
          <Title order={5}>
            {getTranslation("LABEL_OBJECT_INFORMATION") || "Object information"}
          </Title>
          <Badge color={statusColor(data.selfDeliveryStatus)} variant="light">
            {statusLabel(data.selfDeliveryStatus)}
          </Badge>
        </Group>
        <Group grow align="flex-start">
          <Text size="sm">
            <b>{getTranslation("LABEL_OBJECT") || "Object"}:</b> {data.location}
          </Text>
          <Text size="sm">
            <b>{getTranslation("LABEL_CREATOR") || "Creator"}:</b>{" "}
            {data.modifierName}
          </Text>
          <Text size="sm">
            <b>{getTranslation("LABEL_APPROVER") || "Approver"}:</b>{" "}
            {data.acceptedByName}
          </Text>
        </Group>
        <Group grow align="flex-start" mt="xs">
          <Text size="sm">
            <b>{getTranslation("LABEL_SUBSCRIBER") || "Subscriber"}:</b>{" "}
            {data.clientName}
          </Text>
          <Text size="sm">
            <b>{getTranslation("LABEL_DATE") || "Date"}:</b>{" "}
            {formatDate(data.lastModified)}
          </Text>
          <Text size="sm">
            <b>{getTranslation("LABEL_APPROVED_DATE") || "Approved date"}:</b>{" "}
            {formatDate(data.acceptedDate)}
          </Text>
        </Group>
      </Paper>

      {/* Sub-objects */}
      {data.subObjects.length > 0 && (
        <Paper withBorder p="md" radius="md">
          <Title order={5} mb="sm">
            {getTranslation("LABEL_SUB_OBJECTS") || "Sub-objects"}
          </Title>
          <Table striped withTableBorder withColumnBorders>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>
                  {getTranslation("LABEL_SUB_OBJECT_NAME") || "Sub-object name"}
                </Table.Th>
                <Table.Th style={{ width: 140 }}>
                  {getTranslation("LABEL_SUB_OBJECT_DATE") || "Date"}
                </Table.Th>
                <Table.Th style={{ width: 100 }}>
                  {getTranslation("LABEL_INCLUDED") || "Included"}
                </Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {data.subObjects.map((sub) => (
                <Table.Tr key={sub.id}>
                  <Table.Td>{sub.name}</Table.Td>
                  <Table.Td>{formatDate(sub.createdDate)}</Table.Td>
                  <Table.Td>
                    <Checkbox
                      checked={subObjectIds.includes(sub.id)}
                      disabled={!editable}
                      onChange={(e) => {
                        const checked = e.currentTarget.checked;
                        setSubObjectIds((ids) =>
                          checked
                            ? [...ids, sub.id]
                            : ids.filter((id) => id !== sub.id),
                        );
                      }}
                    />
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Paper>
      )}

      {/* Review checklists */}
      {renderChecklist(
        getTranslation("LABEL_OBJECT_REVIEW") || "Object review",
        reviewChecks,
        setReviewChecks,
      )}
      {renderChecklist(
        getTranslation("LABEL_PAVEMENT_REVIEW") || "Pavement review",
        pavementChecks,
        setPavementChecks,
      )}

      {/* Mass / cost information */}
      <Paper withBorder p="md" radius="md">
        <Title order={5} mb="sm">
          {getTranslation("LABEL_MASS_INFORMATION") || "Mass information"}
        </Title>
        <Textarea
          label={getTranslation("LABEL_PAVEMENT_INFORMATION") || "Pavement information"}
          autosize
          minRows={2}
          value={pavementInformation}
          disabled={!editable}
          onChange={(e) => setPavementInformation(e.currentTarget.value)}
        />
        <Group grow mt="sm" align="flex-start">
          <NumberInput
            label={getTranslation("LABEL_STREET_PERMIT_COST") || "Street permit (€)"}
            decimalScale={2}
            value={streetPermitCost}
            disabled={!editable}
            onChange={setStreetPermitCost}
          />
          <NumberInput
            label={getTranslation("LABEL_OTHER_COST") || "Other (€)"}
            decimalScale={2}
            value={otherCost}
            disabled={!editable}
            onChange={setOtherCost}
          />
        </Group>
        <Textarea
          mt="sm"
          label={getTranslation("LABEL_OTHER_COST_INFO") || "Other cost information"}
          autosize
          minRows={2}
          value={otherCostInformation}
          disabled={!editable}
          onChange={(e) => setOtherCostInformation(e.currentTarget.value)}
        />
        <Textarea
          mt="sm"
          label={getTranslation("LABEL_ROAD_MARKING_INFO") || "Road marking information"}
          autosize
          minRows={2}
          value={roadMarkingInformation}
          disabled={!editable}
          onChange={(e) => setRoadMarkingInformation(e.currentTarget.value)}
        />
        <Textarea
          mt="sm"
          label={getTranslation("LABEL_SUB_OBJECT_INFO") || "Other sub-objects information"}
          autosize
          minRows={2}
          value={subObjectInformation}
          disabled={!editable}
          onChange={(e) => setSubObjectInformation(e.currentTarget.value)}
        />
      </Paper>

      {/* Reports verification */}
      <Paper withBorder p="md" radius="md">
        <Title order={5} mb="sm">
          {getTranslation("LABEL_REPORTS") || "Reports"}
        </Title>
        <Group>
          <Checkbox
            label={getTranslation("LABEL_AREA_OK") || "Area boundary OK"}
            checked={areaChecked}
            disabled={!editable}
            onChange={(e) => setAreaChecked(e.currentTarget.checked)}
          />
          <Checkbox
            label={getTranslation("LABEL_SCHEDULE_OK") || "Schedule OK"}
            checked={scheduleChecked}
            disabled={!editable}
            onChange={(e) => setScheduleChecked(e.currentTarget.checked)}
          />
          <Checkbox
            label={getTranslation("LABEL_MATERIALS_OK") || "Mass information OK"}
            checked={materialsChecked}
            disabled={!editable}
            onChange={(e) => setMaterialsChecked(e.currentTarget.checked)}
          />
        </Group>
        <Textarea
          mt="sm"
          label={getTranslation("LABEL_CHECK_INFO") || "Additional information"}
          autosize
          minRows={2}
          value={checkInformation}
          disabled={!editable}
          onChange={(e) => setCheckInformation(e.currentTarget.value)}
        />
        <Textarea
          mt="sm"
          label={getTranslation("LABEL_DEVIATION_REPORT") || "Deviation report"}
          autosize
          minRows={2}
          value={deviationInformation}
          disabled={!editable}
          onChange={(e) => setDeviationInformation(e.currentTarget.value)}
        />
      </Paper>

      {/* Action buttons */}
      {(showSaveButtons || showApproveButtons || showReleaseButton) && (
        <Group>
          {showSaveButtons && (
            <>
              <Button
                variant="default"
                loading={saving}
                onClick={() => handleSave(false)}
              >
                {getTranslation("BUTTON_SAVE") || "Save"}
              </Button>
              <Button loading={saving} onClick={() => handleSave(true)}>
                {getTranslation("BUTTON_SAVE_FINAL") || "Save final"}
              </Button>
            </>
          )}
          {showApproveButtons && (
            <>
              <Button
                color="green"
                loading={saving}
                onClick={() => handleWorkflow("approve")}
              >
                {getTranslation("BUTTON_APPROVE") || "Approve"}
              </Button>
              <Button
                color="orange"
                variant="light"
                loading={saving}
                onClick={() => handleWorkflow("release")}
              >
                {getTranslation("BUTTON_RETURN") || "Return"}
              </Button>
            </>
          )}
          {showReleaseButton && (
            <Button
              color="orange"
              loading={saving}
              onClick={() => handleWorkflow("release")}
            >
              {getTranslation("BUTTON_RELEASE") || "Release"}
            </Button>
          )}
        </Group>
      )}

      {/* Comments */}
      {canShowComments && (
        <Paper withBorder p="md" radius="md">
          <Title order={5} mb="sm">
            {getTranslation("LABEL_COMMENTS") || "Comments"}
          </Title>
          <Stack gap="xs">
            {data.comments.map((comment) => (
              <div key={comment.id}>
                <Group gap="xs">
                  <Text size="sm" fw={600}>
                    {comment.creatorName}
                  </Text>
                  <Text size="xs" c="dimmed">
                    {formatDate(comment.created)}
                  </Text>
                </Group>
                <Text size="sm" style={{ whiteSpace: "pre-wrap" }}>
                  {comment.commentText}
                </Text>
                <Divider mt="xs" />
              </div>
            ))}
            {data.comments.length === 0 && (
              <Text size="sm" c="dimmed">
                {getTranslation("LABEL_NO_COMMENTS") || "No comments yet."}
              </Text>
            )}
          </Stack>
          {data.id > 0 && (
            <>
              <Textarea
                mt="sm"
                label={getTranslation("LABEL_COMMENT_TEXT") || "Comment"}
                autosize
                minRows={2}
                value={newComment}
                onChange={(e) => setNewComment(e.currentTarget.value)}
              />
              <Group mt="sm">
                <Button
                  loading={saving}
                  disabled={!newComment.trim()}
                  onClick={handleAddComment}
                >
                  {getTranslation("BUTTON_SAVE_COMMENT") || "Save comment"}
                </Button>
              </Group>
            </>
          )}
        </Paper>
      )}
    </Stack>
  );
}
